import React, { useEffect, useState } from "react";
import { Plus, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";
import api, { taka } from "@/lib/api";
import { Modal, Field, inputCls, Th, Td, Empty, fmtDate } from "@/pages/admin/AdminUI";

const INV_LABEL = { paddy: "ধান", rice: "চাল", broken: "খুদ", bran: "ব্রান", husk: "তুষ", powder: "গুঁড়া", other: "অন্যান্য" };
const TXN_LABEL = { opening: "ওপেনিং", incoming: "ক্রয়/ইনকামিং", purchase: "ধান ক্রয়", production_in: "প্রোডাকশনে ব্যবহৃত", production_out: "প্রোডাকশন আউটপুট", sale: "বিক্রয়", order_confirm: "অর্ডার কনফার্ম", order_cancel: "অর্ডার বাতিল", damage: "ড্যামেজ/লস", adjust: "সমন্বয়" };

export default function AdminStock() {
  const [summary, setSummary] = useState({});
  const [products, setProducts] = useState([]);
  const [txns, setTxns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", delta: 0, type: "incoming", note: "" });

  const load = () => {
    api.get("/admin/stock/summary").then((r) => setSummary(r.data));
    api.get("/products", { params: { include_inactive: true } }).then((r) => setProducts(r.data));
    api.get("/admin/stock/transactions").then((r) => setTxns(r.data));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.product_id) return toast.error("পণ্য নির্বাচন করুন");
    try { await api.post("/admin/stock/adjust", { ...form, delta: Number(form.delta) }); toast.success("স্টক আপডেট হয়েছে"); setOpen(false); setForm({ product_id: "", delta: 0, type: "incoming", note: "" }); load(); }
    catch (e) { toast.error("ত্রুটি"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[#1E3A2B]">স্টক / ইনভেন্টরি</h1>
        <button onClick={() => setOpen(true)} className="bg-[#1E3A2B] text-white h-11 px-4 rounded-xl flex items-center gap-2 font-medium" data-testid="stock-adjust-btn"><Plus size={18} /> স্টক সমন্বয়</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(summary).map(([k, v]) => (
          <div key={k} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`inv-${k}`}>
            <div className="text-sm text-stone-500">{INV_LABEL[k] || k}</div>
            <div className="text-xl font-bold text-[#1E3A2B]">{Math.round(v.qty)}</div>
            <div className="text-xs text-emerald-700">{taka(v.value)}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <div className="p-4 font-bold text-[#1E3A2B]">বর্তমান স্টক</div>
        <table className="w-full min-w-[600px]">
          <thead className="bg-stone-50 border-y border-stone-200"><tr><Th>পণ্য</Th><Th>টাইপ</Th><Th>স্টক</Th><Th>সর্বনিম্ন</Th><Th>অবস্থা</Th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {products.map((p) => (
              <tr key={p.id}>
                <Td className="font-medium">{p.name_bn}</Td>
                <Td>{INV_LABEL[p.inventory_key]}</Td>
                <Td className="font-semibold">{Math.round(p.stock)} {p.unit}</Td>
                <Td>{p.min_stock}</Td>
                <Td>{p.stock <= p.min_stock ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">⚠️ কম</span> : <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">ঠিক আছে</span>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <div className="p-4 font-bold text-[#1E3A2B]">স্টক লেনদেন (সর্বশেষ)</div>
        <table className="w-full min-w-[600px]">
          <thead className="bg-stone-50 border-y border-stone-200"><tr><Th>তারিখ</Th><Th>পণ্য</Th><Th>ধরন</Th><Th>পরিবর্তন</Th><Th>ব্যালেন্স</Th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {txns.slice(0, 50).map((t) => (
              <tr key={t.id}>
                <Td>{fmtDate(t.created_at)}</Td>
                <Td>{t.product_name}</Td>
                <Td>{TXN_LABEL[t.type] || t.type}</Td>
                <Td className={t.delta >= 0 ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>
                  <span className="inline-flex items-center gap-1">{t.delta >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}{Math.abs(t.delta)}</span>
                </Td>
                <Td>{Math.round(t.balance)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        {txns.length === 0 && <Empty />}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="স্টক সমন্বয়">
        <div className="space-y-3">
          <Field label="পণ্য"><select className={inputCls} value={form.product_id} onChange={(e) => set("product_id", e.target.value)} data-testid="sf-product"><option value="">নির্বাচন করুন</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name_bn} ({Math.round(p.stock)} {p.unit})</option>)}</select></Field>
          <Field label="ধরন"><select className={inputCls} value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="sf-type"><option value="incoming">নতুন স্টক (যোগ)</option><option value="damage">ড্যামেজ/লস (বাদ)</option><option value="adjust">সমন্বয় (+/-)</option></select></Field>
          <Field label="পরিমাণ"><input type="number" className={inputCls} value={form.delta} onChange={(e) => set("delta", e.target.value)} data-testid="sf-delta" /></Field>
          <Field label="নোট"><input className={inputCls} value={form.note} onChange={(e) => set("note", e.target.value)} /></Field>
          <button onClick={save} className="w-full bg-[#1E3A2B] text-white h-11 rounded-xl font-medium" data-testid="save-stock-btn">সংরক্ষণ</button>
        </div>
      </Modal>
    </div>
  );
}
